import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  LinearProgress,
  Alert,
  Snackbar,
  Grid,
  Tooltip
} from "@mui/material";
import {
  X,
  Camera,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Eye,
  Trash2,
  Calendar,
  Image as ImageIcon,
  RefreshCw,
  MapPin,
  Smartphone,
  CloudUpload
} from "lucide-react";
import {
  getInstituteUploadPresignedUrl,
  uploadFileToS3,
  createInstituteEventUploadRequest,
  fetchInstituteEventUploads,
  getFilePresignedViewUrl,
  deleteS3Object,
  deleteInstituteEventUpload
} from "../../../../services/eventUploadsService";
import FilePreviewLightbox from "../../../Desiner/UpcomingEvents/components/FilePreviewLightbox";
import LiveCameraModal from "./LiveCameraModal";
import {
  getDeviceLocation,
  checkGeoPhotoUploadEligibility,
  isLaptopOrDesktopDevice
} from "./geoTagWatermarkUtil";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
};

const GeoPhotoUploadModal = ({
  open,
  onClose,
  booking,
  eventType = "fdp",
  onSuccess
}) => {
  const [existingData, setExistingData] = useState({
    bookslot_id: null,
    request_id: null,
    status: null,
    files: []
  });
  const [previewUrls, setPreviewUrls] = useState({});
  const [stagedFiles, setStagedFiles] = useState([]);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  // Seamless GPS Location state (fetched in background)
  const [gpsCoords, setGpsCoords] = useState(null);
  const [isLiveCameraOpen, setIsLiveCameraOpen] = useState(false);
  const bookslotId = booking?.bookslot_id || booking?.id || booking?.bookslotId;

  // Detect laptop/desktop device to restrict camera capture
  const isLaptop = isLaptopOrDesktopDevice();

  // Seamlessly obtain GPS location and reverse-geocoded place in the background
  const fetchGpsInBackground = async () => {
    try {
      const coords = await getDeviceLocation();
      setGpsCoords(coords);
      return coords;
    } catch (err) {
      console.warn("Background GPS fetch note:", err.message);
      return null;
    }
  };

  // Load existing uploaded files for the bookslot
  const loadExistingUploads = async (slotId) => {
    if (!slotId) return;
    setIsLoadingExisting(true);
    setErrorMessage("");
    try {
      const data = await fetchInstituteEventUploads(slotId);
      const safeData = data || {
        bookslot_id: slotId,
        request_id: null,
        status: null,
        files: []
      };
      setExistingData(safeData);
    } catch (err) {
      console.error("Failed to load existing event uploads:", err);
      setErrorMessage(err.message || "Failed to load uploaded photos.");
    } finally {
      setIsLoadingExisting(false);
    }
  };

  useEffect(() => {
    if (open && bookslotId) {
      setStagedFiles([]);
      setErrorMessage("");
      loadExistingUploads(bookslotId);
      fetchGpsInBackground();
    }
  }, [open, bookslotId]);

  if (!booking) return null;

  const existingFiles = existingData.files || [];
  const status = existingData.status;
  const normalizedStatus = String(status || "").trim().toLowerCase();

  const eligibility = checkGeoPhotoUploadEligibility(booking, eventType);
  const isUploadAllowed = eligibility.canUpload;

  // Do not disable uploads after submission — users need to upload photos across multiple days (e.g. 3 days)
  const isReadOnly = !isUploadAllowed;

  const domainTitle =
    booking.domain_name ||
    booking.domain ||
    booking.program_name ||
    "Event Photos";

  const eventTypeLabel =
    eventType === "tech_camp" || eventType === "Tech Camp"
      ? "Tech Camp"
      : "FDP";

  const instituteName =
    booking.institute_name ||
    booking.institution_name ||
    booking.college_name ||
    "EduSkills Institute";

  // Upload a stamped photo to S3
  const uploadSinglePhoto = async (fileObj, coords) => {
    if (isReadOnly) return;
    setErrorMessage("");

    const entryId = `staged-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
    const newEntry = {
      id: entryId,
      name: fileObj.name,
      size: (fileObj.size / (1024 * 1024)).toFixed(2),
      type: fileObj.type || "image/jpeg",
      preview: URL.createObjectURL(fileObj),
      fileObj: fileObj,
      coords: coords || null,
      address: coords?.address || "",
      key_name: null,
      presigned_url: null,
      uploadProgress: 0,
      isUploading: true,
      isSuccess: false,
      error: null
    };

    setStagedFiles((prev) => [...prev, newEntry]);

    try {
      const presignedData = await getInstituteUploadPresignedUrl(newEntry.name);
      const { presigned_url, key_name } = presignedData || {};

      if (!presigned_url || !key_name) {
        throw new Error("No presigned upload URL or key returned from server.");
      }

      await uploadFileToS3(presigned_url, newEntry.fileObj, (progress) => {
        setStagedFiles((prev) =>
          prev.map((item) =>
            item.id === entryId ? { ...item, uploadProgress: progress } : item
          )
        );
      });

      setStagedFiles((prev) =>
        prev.map((item) =>
          item.id === entryId
            ? {
                ...item,
                key_name,
                presigned_url,
                isUploading: false,
                isSuccess: true,
                uploadProgress: 100
              }
            : item
        )
      );

      setSnackbar({
        open: true,
        message: "Geo-tagged photo captured & uploaded successfully!",
        severity: "success"
      });
      return true;
    } catch (uploadErr) {
      console.error(`Error uploading ${newEntry.name}:`, uploadErr);
      setStagedFiles((prev) =>
        prev.map((item) =>
          item.id === entryId
            ? {
                ...item,
                isUploading: false,
                isSuccess: false,
                error: uploadErr.message || "Upload to S3 failed"
              }
            : item
        )
      );
      setErrorMessage(`Failed to upload ${newEntry.name} to storage.`);
      return false;
    }
  };

  // Callback from LiveCameraModal when photo is snapped
  const handleLivePhotoCaptured = async (blob, coords) => {
    const fileName = `geotag_${Date.now()}_${Math.random().toString(36).substr(2, 5)}.jpg`;
    const file = new File([blob], fileName, { type: "image/jpeg" });
    await uploadSinglePhoto(file, coords);
  };

  // Single Unified Camera Trigger - Opens Live Camera viewfinder directly
  const handleCapturePhotoClick = async () => {
    if (isReadOnly || !isUploadAllowed) {
      if (!isUploadAllowed) {
        setErrorMessage(eligibility.message);
      }
      return;
    }
    if (isLaptop) {
      setErrorMessage(
        "Camera capture is restricted on laptops and desktop computers. Please open this portal on a mobile phone or tablet to capture on-site geo-tagged photos."
      );
      return;
    }
    setErrorMessage("");
    if (!gpsCoords) {
      fetchGpsInBackground();
    }
    setIsLiveCameraOpen(true);
  };

  // Remove staged file
  const handleRemoveStagedFile = async (id) => {
    const target = stagedFiles.find((f) => f.id === id);
    if (target?.key_name) {
      try {
        await deleteS3Object(target.key_name);
      } catch (err) {
        console.warn("Failed to delete staged file from S3:", err);
      }
    }
    setStagedFiles((prev) => prev.filter((item) => item.id !== id));
  };

  // Delete existing photo
  const handleDeleteExistingPhoto = async (file) => {
    if (isReadOnly) return;
    setDeletingId(file.id);
    try {
      if (file?.id && typeof file.id === "number") {
        await deleteInstituteEventUpload(file.id);
      } else if (file?.key_name) {
        await deleteS3Object(file.key_name);
      }

      setExistingData((prev) => ({
        ...prev,
        files: (prev.files || []).filter((f) => f.id !== file.id)
      }));
      setSnackbar({
        open: true,
        message: "Photo deleted successfully.",
        severity: "success"
      });
    } catch (err) {
      console.error("Failed to delete photo:", err);
      setErrorMessage(err.message || "Failed to delete photo. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // Submit all valid photos
  const handleSubmitPhotos = async () => {
    if (isReadOnly) return;
    const isAnyUploading = stagedFiles.some((f) => f.isUploading);
    if (isAnyUploading) {
      setErrorMessage("Please wait for all photos to finish uploading.");
      return;
    }

    const validFiles = stagedFiles.filter((f) => f.key_name && f.isSuccess);

    // Require at least one newly captured photo to submit
    if (validFiles.length === 0) {
      setErrorMessage("Please capture at least one geo-tagged photo before submitting.");
      return;
    }

    if (!bookslotId) {
      setErrorMessage("Missing bookslot ID for this event.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const payload = {
        bookslot_id: Number(bookslotId),
        files: validFiles.map((f) => ({
          file_name: f.name,
          key_name: f.key_name
        }))
      };

      const res = await createInstituteEventUploadRequest(payload);
      setStagedFiles([]);

      const successMsg = res?.message?.trim() || "Photos uploaded successfully!";
      setSnackbar({
        open: true,
        message: successMsg,
        severity: "success"
      });

      if (onSuccess) {
        onSuccess(res);
      }

      await loadExistingUploads(bookslotId);
      onClose();
    } catch (err) {
      console.error("Failed to submit event upload request:", err);
      setErrorMessage(
        err.response?.data?.detail ||
          err.message ||
          "Failed to submit photos. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Preview an existing or staged file in lightbox
  const handlePreviewPhoto = async (file) => {
    let url = file.preview || previewUrls[file.id] || previewUrls[file.key_name];

    if (!url && file.key_name) {
      setLoadingPreviewId(file.id);
      try {
        url = await getFilePresignedViewUrl(file.key_name);
        if (url) {
          setPreviewUrls((prev) => ({
            ...prev,
            [file.id]: url,
            [file.key_name]: url
          }));
        }
      } catch (e) {
        console.error("Preview URL fetch failed:", e);
        setErrorMessage("Failed to load photo preview.");
      } finally {
        setLoadingPreviewId(null);
      }
    }

    if (url) {
      setPreviewFile({
        id: file.id,
        name: file.file_name || file.name,
        size: file.size ? `${file.size} MB` : "Photo",
        url: url,
        previewUrl: url,
        uploadedAt: file.uploaded_at ? formatDate(file.uploaded_at) : "Just now",
        type: "image/jpeg"
      });
    }
  };

  const isUploadingAny = stagedFiles.some((f) => f.isUploading);
  const validStagedCount = stagedFiles.filter((f) => f.key_name && f.isSuccess).length;
  const totalPhotosCount = existingFiles.length + stagedFiles.length;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "10px", sm: "14px" },
            overflow: "hidden",
            m: { xs: 1, sm: 2 },
            width: { xs: "calc(100% - 16px)", sm: "auto" },
            maxHeight: "94vh",
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)"
          }
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "1px solid #e2e8f0",
            py: { xs: 1.5, sm: 1.75 },
            px: { xs: 2, sm: 3 },
            backgroundColor: "#f8fafc"
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                mb: 0.5,
                flexWrap: "wrap"
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  backgroundColor: "#eff6ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#1877f2",
                  border: "1px solid #bfdbfe"
                }}
              >
                <Camera size={18} />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.05rem" }}
              >
                Upload Geo-Tagged Photos
              </Typography>
              <Chip
                label={eventTypeLabel}
                size="small"
                sx={{
                  height: "20px",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  backgroundColor:
                    eventTypeLabel === "FDP" ? "#eff6ff" : "#f0fdf4",
                  color: eventTypeLabel === "FDP" ? "#1877f2" : "#16a34a",
                  border: `1px solid ${
                    eventTypeLabel === "FDP" ? "#bfdbfe" : "#bbf7d0"
                  }`,
                  borderRadius: "4px"
                }}
              />
              {status && (
                <Chip
                  icon={
                    normalizedStatus === "approved" || normalizedStatus === "accepted" ? (
                      <CheckCircle2 size={12} color="#16a34a" />
                    ) : normalizedStatus === "rejected" ? (
                      <AlertTriangle size={12} color="#dc2626" />
                    ) : (
                      <Clock size={12} color="#d97706" />
                    )
                  }
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    textTransform: "capitalize",
                    backgroundColor:
                      normalizedStatus === "approved" || normalizedStatus === "accepted"
                        ? "#f0fdf4"
                        : normalizedStatus === "rejected"
                        ? "#fef2f2"
                        : "#fffbeb",
                    color:
                      normalizedStatus === "approved" || normalizedStatus === "accepted"
                        ? "#16a34a"
                        : normalizedStatus === "rejected"
                        ? "#dc2626"
                        : "#d97706",
                    border: `1px solid ${
                      normalizedStatus === "approved" || normalizedStatus === "accepted"
                        ? "#bbf7d0"
                        : normalizedStatus === "rejected"
                        ? "#fecaca"
                        : "#fde68a"
                    }`,
                    borderRadius: "4px"
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.75,
                color: "#64748b",
                fontSize: { xs: "0.72rem", sm: "0.78rem" },
                pl: { xs: 0, sm: 5.25 },
                mt: { xs: 0.5, sm: 0 }
              }}
            >
              <Typography
                component="span"
                sx={{ fontWeight: 600, color: "#475569", fontSize: "0.78rem" }}
              >
                {domainTitle}
              </Typography>
              {instituteName && (
                <>
                  <span>•</span>
                  <span>{instituteName}</span>
                </>
              )}
              {booking.start_date && (
                <>
                  <span>•</span>
                  <Calendar size={12} color="#1877f2" />
                  <span>
                    {booking.start_date}
                    {booking.end_date ? ` – ${booking.end_date}` : ""}
                  </span>
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Refresh Uploads">
              <IconButton
                onClick={() => loadExistingUploads(bookslotId)}
                size="small"
                sx={{ color: "#64748b" }}
              >
                <RefreshCw size={15} />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small" sx={{ color: "#94a3b8" }}>
              <X size={18} />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Modal Body */}
        <DialogContent
          dividers
          sx={{
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            backgroundColor: "#ffffff",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#cbd5e1",
              borderRadius: "4px"
            }
          }}
        >
          {/* Status Banners */}
          {normalizedStatus === "rejected" && (
            <Alert
              severity="warning"
              icon={<AlertTriangle size={18} />}
              sx={{ borderRadius: "8px", fontSize: "0.8rem", py: 0.75 }}
            >
              Your previous photo submission was rejected. You can delete rejected photos and capture new geo-tagged photos below.
            </Alert>
          )}

          {(normalizedStatus === "approved" || normalizedStatus === "accepted") && (
            <Alert
              severity="success"
              icon={<CheckCircle2 size={18} />}
              sx={{ borderRadius: "8px", fontSize: "0.8rem", py: 0.75 }}
            >
              Previous photo submission has been approved. You can continue capturing and uploading geo-tagged photos for each day of the event.
            </Alert>
          )}

          {normalizedStatus === "pending" && (
            <Alert
              severity="info"
              icon={<Clock size={18} />}
              sx={{ borderRadius: "8px", fontSize: "0.8rem", py: 0.75 }}
            >
              Photo submission under review ({existingFiles.length} photo{existingFiles.length !== 1 ? "s" : ""} uploaded). You can continue capturing and uploading geo-tagged photos for the 3 days.
            </Alert>
          )}

          {!isUploadAllowed && (
            <Alert
              severity="warning"
              icon={<Clock size={18} />}
              sx={{ borderRadius: "8px", fontSize: "0.8rem", py: 0.75 }}
            >
              {eligibility.message}
            </Alert>
          )}

          {isUploadAllowed && !isReadOnly && eligibility.reminderMessage && (
            <Alert
              severity="info"
              icon={<Clock size={18} />}
              sx={{ borderRadius: "8px", fontSize: "0.8rem", py: 0.75 }}
            >
              <strong>Notice:</strong> {eligibility.reminderMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert
              severity="error"
              onClose={() => setErrorMessage("")}
              sx={{ borderRadius: "8px", fontSize: "0.8rem", py: 0.5 }}
            >
              {errorMessage}
            </Alert>
          )}

          {/* Simple, Clean Camera Capture Hub (Camera Only) */}
          {!isReadOnly && (
            <Box
              sx={{
                border: isLaptop ? "2px dashed #f59e0b" : "2px solid #bfdbfe",
                borderRadius: "14px",
                background: isLaptop
                  ? "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)"
                  : "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)",
                p: { xs: 2.5, sm: 3 },
                textAlign: "center"
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  backgroundColor: isLaptop ? "#fef3c7" : "#dbeafe",
                  color: isLaptop ? "#d97706" : "#1877f2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.25,
                  boxShadow: isLaptop
                    ? "0 4px 12px rgba(217, 119, 6, 0.15)"
                    : "0 4px 12px rgba(24, 119, 242, 0.15)"
                }}
              >
                {isLaptop ? <Smartphone size={26} /> : <Camera size={26} />}
              </Box>

              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "0.95rem", sm: "1.02rem" },
                  color: "#0f172a",
                  mb: 0.5
                }}
              >
                {isLaptop
                  ? "Camera Capture Restricted on Laptop"
                  : "Capture Geo-Tagged Photos (Camera Only)"}
              </Typography>

              {/* Informative restriction notice when opened on laptop/desktop */}
              {isLaptop && (
                <Alert
                  severity="warning"
                  icon={<Smartphone size={18} />}
                  sx={{
                    my: 1.5,
                    maxWidth: 560,
                    mx: "auto",
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    border: "1px solid #fde68a",
                    color: "#92400e",
                    fontSize: "0.78rem",
                    textAlign: "left",
                    "& .MuiAlert-icon": { color: "#d97706" }
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", mb: 0.25 }}>
                    Mobile Device Required
                  </Typography>
                  Live geo-tagged photos must be taken directly on-site using a smartphone or mobile tablet camera. Please open this portal on your mobile device to capture event photos.
                </Alert>
              )}

              {eligibility.formattedEndDate && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "#b45309",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    mb: gpsCoords?.address ? 1 : 1.75
                  }}
                >
                  ⏰ Note: You can only upload photos till the event end date ({eligibility.formattedEndDate}). Later it will not be eligible to upload.
                </Typography>
              )}

              {/* Seamless Location Preview if available in background */}
              {gpsCoords?.address && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "#e0f2fe",
                    color: "#0369a1",
                    px: 1.5,
                    py: 0.4,
                    borderRadius: "20px",
                    fontSize: "0.74rem",
                    fontWeight: 600,
                    mb: 2
                  }}
                >
                  <MapPin size={13} color="#0284c7" />
                  <span>Detected Location: {gpsCoords.address}</span>
                </Box>
              )}

              {/* Single Camera Trigger Button - Only rendered on mobile/tablet */}
              {!isLaptop && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 0.5
                  }}
                >
                  <Tooltip
                    title={!isUploadAllowed ? eligibility.message : ""}
                    arrow
                  >
                    <span>
                      <Button
                        variant="contained"
                        size="large"
                        disabled={!isUploadAllowed}
                        startIcon={<Camera size={20} />}
                        onClick={handleCapturePhotoClick}
                        sx={{
                          textTransform: "none",
                          borderRadius: "10px",
                          backgroundColor: "#1877f2",
                          color: "#ffffff",
                          fontWeight: 700,
                          fontSize: "0.92rem",
                          px: { xs: 3.5, sm: 4.5 },
                          py: 1.25,
                          boxShadow: "0 4px 16px rgba(24, 119, 242, 0.35)",
                          "&:hover": {
                            backgroundColor: "#1465cc",
                            boxShadow: "0 6px 20px rgba(24, 119, 242, 0.45)"
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "#cbd5e1",
                            color: "#64748b"
                          }
                        }}
                      >
                        Take Photo with Camera
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}

          {/* Loading Existing Photos Indicator */}
          {isLoadingExisting && (
            <Box
              sx={{
                py: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5
              }}
            >
              <CircularProgress size={28} sx={{ color: "#1877f2" }} />
              <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
                Loading event photos...
              </Typography>
            </Box>
          )}

          {/* Empty state when read-only and no photos */}
          {!isLoadingExisting && isReadOnly && totalPhotosCount === 0 && (
            <Box sx={{ py: 3, textAlign: "center", color: "#64748b" }}>
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                No geo-tagged photos uploaded for this event.
              </Typography>
            </Box>
          )}

          {/* Photos Cards Section */}
          {!isLoadingExisting && totalPhotosCount > 0 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.88rem",
                    color: "#0f172a",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75
                  }}
                >
                  <ImageIcon size={16} color="#1877f2" />
                  Geo-Tagged Event Photos ({totalPhotosCount})
                </Typography>

                {!isReadOnly && stagedFiles.length > 0 && (
                  <Button
                    size="small"
                    color="error"
                    disabled={isUploadingAny || isSubmitting}
                    onClick={() => {
                      stagedFiles.forEach((f) => {
                        if (f.key_name) deleteS3Object(f.key_name).catch(() => {});
                      });
                      setStagedFiles([]);
                    }}
                    sx={{ textTransform: "none", fontSize: "0.74rem" }}
                  >
                    Clear New ({stagedFiles.length})
                  </Button>
                )}
              </Box>

              <Grid container spacing={1.5}>
                {/* 1. Staged / Newly Uploaded Photos with Immediate Preview & Delete */}
                {stagedFiles.map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: "10px",
                        border: "1px solid #bfdbfe",
                        backgroundColor: "#f8fafc",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: 1,
                        position: "relative"
                      }}
                    >
                      {/* Image Thumbnail with Geo-Tag Badge */}
                      <Box
                        onClick={() => handlePreviewPhoto(item)}
                        sx={{
                          width: "100%",
                          height: 120,
                          borderRadius: "6px",
                          overflow: "hidden",
                          backgroundColor: "#f1f5f9",
                          cursor: "pointer",
                          border: "1px solid #cbd5e1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative"
                        }}
                      >
                        <img
                          src={item.preview}
                          alt={item.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover"
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            left: 6,
                            backgroundColor: "rgba(15, 23, 42, 0.82)",
                            backdropFilter: "blur(4px)",
                            color: "#38bdf8",
                            fontSize: "0.62rem",
                            fontWeight: 700,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4
                          }}
                        >
                          <MapPin size={10} />
                          <span>Geo-Tagged</span>
                        </Box>
                      </Box>

                      {/* Info & Location Display */}
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              color: "#0f172a",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              flex: 1
                            }}
                            title={item.name}
                          >
                            {item.name}
                          </Typography>
                          <IconButton
                            size="small"
                            disabled={item.isUploading}
                            onClick={() => handleRemoveStagedFile(item.id)}
                            sx={{
                              color: "#94a3b8",
                              p: "2px",
                              "&:hover": { color: "#dc2626" }
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Box>

                        {/* Human-Readable Location (e.g., Bhubaneswar, Odisha) */}
                        {(item.address || item.coords?.address) && (
                          <Typography
                            sx={{
                              color: "#0369a1",
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.4,
                              mt: 0.3
                            }}
                          >
                            <MapPin size={11} color="#0284c7" />
                            <span>{item.address || item.coords.address}</span>
                          </Typography>
                        )}

                        {/* GPS Coordinates */}
                        {item.coords && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748b",
                              fontSize: "0.66rem",
                              fontWeight: 500,
                              display: "block",
                              mt: 0.1
                            }}
                          >
                            Lat: {item.coords.latitude.toFixed(4)}°, Long:{" "}
                            {item.coords.longitude.toFixed(4)}°
                            {item.coords.accuracy
                              ? ` (±${Math.round(item.coords.accuracy)}m)`
                              : ""}
                          </Typography>
                        )}

                        <Typography
                          variant="caption"
                          sx={{
                            color: item.error ? "#dc2626" : "#64748b",
                            fontSize: "0.7rem",
                            display: "block",
                            mt: 0.2
                          }}
                        >
                          {item.error
                            ? item.error
                            : item.isSuccess
                            ? `${item.size} MB`
                            : `Uploading... ${item.uploadProgress}%`}
                        </Typography>

                        {item.isUploading && (
                          <LinearProgress
                            variant="determinate"
                            value={item.uploadProgress}
                            sx={{
                              height: 3,
                              borderRadius: 2,
                              mt: 0.5,
                              backgroundColor: "#e2e8f0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "#1877f2"
                              }
                            }}
                          />
                        )}
                      </Box>

                      {/* Action Buttons: Preview and Delete ALWAYS visible */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          pt: 0.75,
                          borderTop: "1px solid #e2e8f0"
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Eye size={12} />}
                          onClick={() => handlePreviewPhoto(item)}
                          sx={{
                            flex: 1,
                            py: 0.35,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            borderRadius: "6px",
                            textTransform: "none",
                            color: "#1877f2",
                            borderColor: "#bfdbfe",
                            backgroundColor: "#ffffff",
                            "&:hover": {
                              backgroundColor: "#eff6ff",
                              borderColor: "#1877f2"
                            }
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Trash2 size={12} />}
                          onClick={() => handleRemoveStagedFile(item.id)}
                          sx={{
                            flex: 1,
                            py: 0.35,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            borderRadius: "6px",
                            textTransform: "none",
                            borderColor: "#fecaca",
                            backgroundColor: "#fef2f2",
                            "&:hover": {
                              backgroundColor: "#fee2e2",
                              borderColor: "#dc2626"
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}

                {/* 2. Existing Uploaded Photos */}
                {existingFiles.map((file) => {
                  const isDeletingThis = deletingId === file.id;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={file.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.25,
                          borderRadius: "10px",
                          border: "1px solid #e2e8f0",
                          backgroundColor: "#ffffff",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          gap: 1,
                          transition: "all 0.15s ease",
                          "&:hover": {
                            borderColor: "#cbd5e1",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                          }
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              color: "#0f172a",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                            title={file.file_name}
                          >
                            {file.file_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748b",
                              fontSize: "0.68rem",
                              display: "block"
                            }}
                          >
                            {formatDate(file.uploaded_at)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            pt: 0.75,
                            borderTop: "1px solid #f1f5f9"
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={loadingPreviewId === file.id}
                            startIcon={
                              loadingPreviewId === file.id ? (
                                <CircularProgress size={12} />
                              ) : (
                                <Eye size={12} />
                              )
                            }
                            onClick={() => handlePreviewPhoto(file)}
                            sx={{
                              flex: 1,
                              py: 0.35,
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              borderRadius: "6px",
                              textTransform: "none",
                              color: "#1877f2",
                              borderColor: "#cbd5e1",
                              "&:hover": {
                                backgroundColor: "#eff6ff",
                                borderColor: "#1877f2"
                              }
                            }}
                          >
                            Preview
                          </Button>

                          {!isReadOnly && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={isDeletingThis}
                              startIcon={
                                isDeletingThis ? (
                                  <CircularProgress size={12} color="inherit" />
                                ) : (
                                  <Trash2 size={12} />
                                )
                              }
                              onClick={() => handleDeleteExistingPhoto(file)}
                              sx={{
                                flex: 1,
                                py: 0.35,
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                borderRadius: "6px",
                                textTransform: "none",
                                borderColor: "#fecaca",
                                backgroundColor: "#fef2f2",
                                "&:hover": {
                                  backgroundColor: "#fee2e2",
                                  borderColor: "#dc2626"
                                }
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </DialogContent>

        {/* Modal Footer */}
        <DialogActions
          sx={{
            py: { xs: 1.25, sm: 1.5 },
            px: { xs: 2, sm: 3 },
            backgroundColor: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              textTransform: "none",
              color: "#64748b",
              fontWeight: 600,
              fontSize: "0.82rem"
            }}
          >
            Close
          </Button>

          {!isReadOnly && (stagedFiles.length > 0 || existingFiles.length > 0) && (
            <Button
              variant="contained"
              disabled={
                isSubmitting ||
                isUploadingAny ||
                validStagedCount === 0
              }
              onClick={handleSubmitPhotos}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CloudUpload size={16} />
                )
              }
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                backgroundColor: "#1877f2",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "0.82rem",
                px: 2.5,
                py: 0.75,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#1465cc",
                  boxShadow: "none"
                }
              }}
            >
              {isSubmitting
                ? "Submitting Photos..."
                : isUploadingAny
                ? "Uploading..."
                : validStagedCount > 0
                ? `Submit Photos (${validStagedCount})`
                : existingFiles.length > 0
                ? `Photos Submitted (${existingFiles.length})`
                : "Submit Photos"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Clean Encapsulated Live Camera Modal */}
      <LiveCameraModal
        open={isLiveCameraOpen}
        onClose={() => setIsLiveCameraOpen(false)}
        onPhotoCaptured={handleLivePhotoCaptured}
        gpsCoords={gpsCoords}
        instituteName={instituteName}
        domainTitle={domainTitle}
        eventTypeLabel={eventTypeLabel}
      />

      {/* Lightbox / High-Res Image Preview Modal */}
      {previewFile && (
        <FilePreviewLightbox
          previewFile={previewFile}
          onClose={() => setPreviewFile(null)}
          userRole="institute"
          showDownload={true}
          onRemoveFile={
            !isReadOnly
              ? (f) => {
                  setPreviewFile(null);
                  if (f.id && typeof f.id === "number") {
                    handleDeleteExistingPhoto(f);
                  } else {
                    handleRemoveStagedFile(f.id);
                  }
                }
              : null
          }
        />
      )}

      {/* Snackbar Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity || "success"}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "8px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GeoPhotoUploadModal;
